import type { NotificationSlice } from 'containers/Notifications/useNotificationSlice';
import useNotificationSlice from 'containers/Notifications/useNotificationSlice';
import {
  useReportViewSlice,
  type ReportViewSlice,
} from 'containers/ReportView/slices/useReportViewSlice';
import {
  useReportingEngineWidgetSlice,
  type ReportingEngineWidgetSlice,
} from 'containers/ReportingEngine/slices/useReportingEngineWidgetSlice';

import {
  useTicketLayoutSlice,
  type TicketLayoutSlice,
} from 'containers/Ticketing/TicketLayouts/slices/useTicketLayoutSlice';
import {
  useTicketFieldSlice,
  type TicketFieldSlice,
} from 'containers/Ticketing/slices/useTicketFieldSlice';
import type { SplashScreenSlice } from 'slices/useSplashScreenSlice';
import type { StudioSlice } from 'slices/useStudioSlice';
import { useStudioSlice } from 'slices/useStudioSlice';
import type { TicketingAttachmentSlice } from 'slices/useTicketingAttachmentSlice';
import { useTicketingAttachmentSlice } from 'slices/useTicketingAttachmentSlice';
import {
  useTicketingDetailSlice,
  type TicketingDetailSlice,
} from 'slices/useTicketingDetailSlice';
import type { TicketingPermissionSlice } from 'slices/useTicketingPermissionSlice';
import { useTicketingPermissionsSlice } from 'slices/useTicketingPermissionSlice';
import type { TicketingSlice } from 'slices/useTicketingSlice';
import { useTicketingSlice } from 'slices/useTicketingSlice';
import type { TicketingThreadSlice } from 'slices/useTicketingThreadSlice';
import { useTicketingThreadSlice } from 'slices/useTicketingThreadSlice';
import type { MessageTimelineSlice } from 'containers/Ticketing/hooks/useMessageTimelineSlice';
import { useMessageTimelineSlice } from 'containers/Ticketing/hooks/useMessageTimelineSlice';
import type { StateCreator } from 'zustand';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useSplashScreenSlice } from 'slices/useSplashScreenSlice';

type State = {
  ticketingThreadSlice: TicketingThreadSlice;
  ticketingAttachmentSlice: TicketingAttachmentSlice;
  ticketingSlice: TicketingSlice;
  ticketingDetailSlice: TicketingDetailSlice;
  notificationSlice: NotificationSlice;
  ticketLayoutSlice: TicketLayoutSlice;
  ticketFieldSlice: TicketFieldSlice;
  reportViewSlice: ReportViewSlice;
  ticketingPermissionsSlice: TicketingPermissionSlice;
  reportingEngineWidgetSlice: ReportingEngineWidgetSlice;
  studioSlice: StudioSlice;
  splashScreenSlice: SplashScreenSlice;
  messageTimelineSlice: MessageTimelineSlice;
};

export type ImmerStateCreator<T> = StateCreator<
  State,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  T
>;

export const useStore = create<State>()(
  immer(
    devtools((...args) => ({
      ticketingThreadSlice: useTicketingThreadSlice(...args),
      ticketingAttachmentSlice: useTicketingAttachmentSlice(...args),
      ticketingSlice: useTicketingSlice(...args),
      ticketingDetailSlice: useTicketingDetailSlice(...args),
      notificationSlice: useNotificationSlice(...args),
      ticketLayoutSlice: useTicketLayoutSlice(...args),
      ticketFieldSlice: useTicketFieldSlice(...args),
      reportViewSlice: useReportViewSlice(...args),
      ticketingPermissionsSlice: useTicketingPermissionsSlice(...args),
      reportingEngineWidgetSlice: useReportingEngineWidgetSlice(...args),
      studioSlice: useStudioSlice(...args),
      splashScreenSlice: useSplashScreenSlice(...args),
      messageTimelineSlice: useMessageTimelineSlice(...args),
    }))
  )
);
